import React, { useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { getColorFromRarity } from '../../utils/rarityUtils';
import { ItemActions } from '../ItemActions';
import hardcoreOff from '../../assets/img/hardcore_off.png';
import hardcoreOn from '../../assets/img/hardcore_on.png';
import ladderOn from '../../assets/img/ladder_on.png';
import ladderOff from '../../assets/img/ladder_off.png';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

export interface ItemData {
  id: number;
  name: string;
  title?: string;
  context?: string;
  d2rshopproductlink?: string;
  fiat_price?: string;
  fg_price?: string;
  product_image_path?: string;
  skin_image_path?: string;
  image_path?: string;
  original_rarity?: string;
  rarity_id?: string;
  stats_description?: string;
  is_product?: boolean;
  ladder?: number;
  hardcore?: number;
}

interface ItemCardProps {
  item: ItemData;
  sessionToken?: string | null;
  variant?: 'default' | 'cart' | 'bubble' | 'rune' | 'fullCart';
  showPrice?: boolean;
  showContext?: boolean;
  onImageClick?: (item: ItemData) => void;
  onShare?: (url: string) => void;
  showRarityIndicator?: boolean;
  itemsPerPage?: number; // Used for conditional copy button logic
  customStyles?: {
    container?: React.CSSProperties;
    imageContainer?: React.CSSProperties;
    statsContainer?: React.CSSProperties;
  };
  actions?: (
    | 'viewDetails'
    | 'viewInShop'
    | 'tools'
    | 'track'
    | 'share'
    | 'addToCart'
    | 'makeOffer'
  )[];
}

const getContextTooltipText = (context: string, ladder?: number, hardcore?: number): string => {
  // Handle string-based contexts first
  if (typeof context === 'string') {
    switch (context.toLowerCase()) {
      case 'scl':
        return 'Softcore Ladder';
      case 'scnl':
        return 'Softcore Non-Ladder';
      case 'hcl':
        return 'Hardcore Ladder';
      case 'hcnl':
        return 'Hardcore Non-Ladder';
      default:
        break;
    }
  }

  // Fallback to numeric ladder/hardcore values
  if (ladder !== undefined && hardcore !== undefined) {
    if (hardcore) {
      return ladder ? 'Hardcore Ladder' : 'Hardcore Non-Ladder';
    } else {
      return ladder ? 'Softcore Ladder' : 'Softcore Non-Ladder';
    }
  }

  return 'Unknown';
};

const renderContextIcons = (context: string, ladder?: number, hardcore?: number) => {
  let isHardcore = false;
  let isLadder = false;

  // Handle string-based contexts first
  if (typeof context === 'string') {
    const ctx = context.toLowerCase();
    isHardcore = ctx === 'hcl' || ctx === 'hcnl';
    isLadder = ctx === 'scl' || ctx === 'hcl';
  } else {
    // Fallback to numeric values
    isHardcore = !!hardcore;
    isLadder = !!ladder;
  }

  return (
    <span
      style={{
        position: 'absolute',
        top: 8,
        left: 8,
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        zIndex: 1,
      }}
    >
      <img
        style={{ width: '24px' }}
        src={isHardcore ? hardcoreOn : hardcoreOff}
        alt={isHardcore ? 'hardcoreOn' : 'hardcoreOff'}
      />
      <img
        style={{ width: '24px' }}
        src={isLadder ? ladderOn : ladderOff}
        alt={isLadder ? 'ladderOn' : 'ladderOff'}
      />
    </span>
  );
};

export const ItemCard: React.FC<ItemCardProps> = ({
  item,
  sessionToken,
  variant = 'default',
  showPrice = true,
  showContext = true,
  onImageClick,
  onShare,
  showRarityIndicator = true,
  itemsPerPage,
  customStyles = {},
  actions = ['viewDetails', 'viewInShop', 'tools', 'track', 'share', 'addToCart', 'makeOffer'],
}) => {
  const { t } = useTranslation();
  const [sharedLink, setSharedLink] = useState('');
  const [sharedItemId, setSharedItemId] = useState<number | null>(null);

  // Internal logic for determining if copy button should be shown
  const shouldShowCopyButton = () => {
    if (itemsPerPage === 4 && item.original_rarity !== 'RUNE') {
      return true;
    }
    if (variant === 'default' && item.original_rarity !== 'RUNE') {
      return true;
    }
    return false;
  };

  const handleImageClick = () => {
    if (onImageClick) {
      onImageClick(item);
    } else {
      // Default behavior
      if (item.d2rshopproductlink && item.d2rshopproductlink !== '') {
        window.open(item.d2rshopproductlink, '_blank');
      } else {
        window.open(
          item.original_rarity === 'RUNE' ? `/product/${item.id}` : `/item/${item.id}`,
          '_blank'
        );
      }
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(item.stats_description || '');
    toast.success(t('common.copiedToClipboard'));
  };

  const handleShare = (url: string) => {
    setSharedLink(url);
    setSharedItemId(item.id);
    if (onShare) {
      onShare(url);
    }
  };

  const getContainerStyles = () => {
    const baseStyles: React.CSSProperties = {
      display: 'flex',
      flexDirection:
        variant === 'cart' || variant === 'fullCart' ? ('row' as const) : ('column' as const),
      alignItems:
        variant === 'cart' || variant === 'fullCart' ? ('stretch' as const) : ('center' as const),
      padding: '8px',
      backgroundColor: 'rgba(0,0,0,0.05)',
      borderRadius: '8px',
      overflow: 'hidden',
      height:
        variant === 'rune'
          ? '355px'
          : variant === 'cart'
            ? '200px'
            : variant === 'fullCart'
              ? '200px'
              : '550px',
      ...customStyles.container,
    };

    switch (variant) {
      case 'cart':
        return {
          ...baseStyles,
          backgroundColor: customStyles.container?.backgroundColor || 'rgba(0,0,0,0.02)',
          border: customStyles.container?.border || '1px solid rgba(0,0,0,0.1)',
          borderRadius: customStyles.container?.borderRadius || '8px',
          height: '200px',
          flexDirection: 'row' as const,
          alignItems: 'stretch' as const,
        };
      case 'fullCart':
        return {
          ...baseStyles,
          backgroundColor: customStyles.container?.backgroundColor || 'rgba(0,0,0,0.02)',
          border: customStyles.container?.border || '1px solid rgba(0,0,0,0.1)',
          borderRadius: customStyles.container?.borderRadius || '8px',
          height: '200px',
          flexDirection: 'row' as const,
          alignItems: 'stretch' as const,
        };
      case 'bubble':
        return {
          ...baseStyles,
          padding: '4px',
          backgroundColor: 'transparent',
          height: '300px',
        };
      case 'rune':
        return {
          ...baseStyles,
          height: '355px',
        };
      default:
        return baseStyles;
    }
  };

  const getImageContainerStyles = () => {
    const baseStyles = {
      backgroundColor: (theme: any) =>
        theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100],
      borderRadius: 1,
      overflow: 'hidden',
      border: (theme: any) => `1px solid ${theme.palette.secondary.main}`,
      display: 'inline-flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      position: 'relative' as const,
      ...customStyles.imageContainer,
    };

    if (variant === 'cart') {
      return {
        ...baseStyles,
        border: 'none',
        borderRadius: 0,
        width: '45%',
        height: '100%',
        flex: 'none',
      };
    }

    if (variant === 'fullCart') {
      return {
        ...baseStyles,
        border: 'none',
        borderRadius: 0,
        width: '50%',
        height: '100%',
        flex: 'none',
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        justifyContent: 'center',
      };
    }

    return {
      ...baseStyles,
      border: 'none',
      borderBottom: (theme: any) => `1px solid ${theme.palette.secondary.main}`,
      borderRadius: 0,
      width: '100%',
      height: '100%',
      flex: 1,
    };
  };

  const getStatsContainerStyles = () => {
    const getStatsHeight = () => {
      switch (variant) {
        case 'rune':
          return '65px';
        case 'cart':
          return '100%';
        case 'fullCart':
          return '100%';
        case 'bubble':
          return '100px';
        default:
          return '270px';
      }
    };

    const baseStyles = {
      p: 2,
      whiteSpace: 'pre-wrap' as const,
      fontFamily: 'monospace',
      color: '#000',
      position: 'relative' as const,
      backgroundColor: (theme: any) =>
        theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100],
      height: getStatsHeight(),
      overflow: 'auto',
      ...customStyles.statsContainer,
    };

    if (variant === 'cart') {
      return {
        ...baseStyles,
        ...customStyles.statsContainer,
        borderLeft: (theme: any) => `1px solid ${theme.palette.secondary.main}`,
        borderRadius: 0,
        width: '51%',
        flex: 'none',
        p: 1.5,
        paddingTop: '18px !important',
        marginTop: '-8px',
      };
    }

    if (variant === 'fullCart') {
      return {
        ...baseStyles,
        ...customStyles.statsContainer,
        borderLeft: (theme: any) => `1px solid ${theme.palette.secondary.main}`,
        borderRadius: 0,
        width: '50%',
        height: '100%',
        flex: 'none',
        p: 2,
        paddingTop: '18px !important',
        marginTop: '-8px',
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'visible',
      };
    }

    return {
      ...baseStyles,
      width: '100%',
    };
  };

  return (
    <div style={getContainerStyles()}>
      {/* Item Actions - Hide for cart and fullCart variants since they have their own controls */}
      {variant !== 'cart' && variant !== 'fullCart' && (
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            padding: '4px',
            borderRadius: '4px',
            border: '1px solid rgba(0,0,0,0.1)',
            backgroundColor: 'rgba(0,0,0,0.02)',
            marginBottom: '8px',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '4px',
              flex: 1,
              justifyContent: 'center',
            }}
          >
            <ItemActions
              item={item}
              sessionToken={sessionToken}
              size="small"
              showViewDetails={actions.includes('viewDetails')}
              showViewInShop={actions.includes('viewInShop')}
              showToolsIcon={actions.includes('tools')}
              showTrack={actions.includes('track')}
              showShare={actions.includes('share')}
              showAddToCart={actions.includes('addToCart')}
              showMakeOffer={actions.includes('makeOffer')}
              onShare={handleShare}
            />
          </div>
        </div>
      )}

      {/* Shared Link Display - Hide for cart and fullCart variants */}
      {variant !== 'cart' && variant !== 'fullCart' && sharedLink && sharedItemId === item.id && (
        <div style={{ width: '100%', textAlign: 'center', marginBottom: '8px' }}>
          <Box
            sx={{
              p: 1,
              mb: 1,
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100],
              borderRadius: 1,
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              color: (theme) => theme.palette.text.primary,
              position: 'relative',
              fontSize: '0.8rem',
            }}
          >
            {sharedLink}
            <Tooltip title={t('common.copyToClipboard')} placement="top">
              <IconButton
                onClick={() => {
                  navigator.clipboard.writeText(sharedLink);
                  toast.success(t('common.copiedToClipboard'));
                }}
                size="small"
                sx={{
                  position: 'absolute',
                  top: -24,
                  right: -8,
                  color: (theme) => theme.palette.text.secondary,
                  '&:hover': {
                    color: (theme) => theme.palette.text.primary,
                  },
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </div>
      )}

      {/* Main Item Container */}
      {variant === 'cart' || variant === 'fullCart' ? (
        <>
          <Box sx={getImageContainerStyles()}>
            {/* Context Icons */}
            {showContext && item?.context && item.context !== '' && (
              <Tooltip
                title={getContextTooltipText(item.context, item.ladder, item.hardcore)}
                arrow
              >
                {renderContextIcons(item.context, item.ladder, item.hardcore)}
              </Tooltip>
            )}

            {/* Price Badge */}
            {showPrice &&
              (() => {
                const fiat = item.fiat_price;
                const fg = item.fg_price;
                if (fiat && fiat !== '' && fiat !== '0.00' && fiat !== '???') {
                  if (!isNaN(parseFloat(fiat)) && !fiat.toLowerCase().includes('fg')) {
                    return (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: (theme) => theme.palette.primary.main,
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                          zIndex: 1,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        }}
                      >
                        {`${parseFloat(fiat).toFixed(2).replace(/\.00$/, '')} $`}
                      </Box>
                    );
                  } else if (fiat.toLowerCase().includes('fg')) {
                    return (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: (theme) => theme.palette.primary.main,
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                          zIndex: 1,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        }}
                      >
                        {fiat}
                      </Box>
                    );
                  }
                } else if (fg && fg !== '' && fg !== '0.00') {
                  return (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: (theme) => theme.palette.primary.main,
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        fontWeight: 'bold',
                        zIndex: 1,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      }}
                    >
                      {fg.toLowerCase().includes('fg') ? fg : `${fg} fg`}
                    </Box>
                  );
                }
                return null;
              })()}

            {/* Item Image */}
            <Tooltip
              title={
                <img
                  src={'https://d2r-member.com/image/' + item.product_image_path}
                  alt={item.title || item.name}
                  style={{
                    objectFit: 'contain',
                    maxWidth: '100%',
                    maxHeight: '300px',
                  }}
                />
              }
              arrow
            >
              <button
                onClick={handleImageClick}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleImageClick();
                  }
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  width: '100%',
                  height:
                    variant === 'cart' || variant === 'fullCart'
                      ? '100%'
                      : variant === 'rune'
                        ? '205px'
                        : '180px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={'https://d2r-member.com/skin/' + item.skin_image_path}
                  alt={item.name}
                  style={{
                    width: 'auto',
                    height: 'auto',
                    maxWidth: '100%',
                    objectFit: 'scale-down',
                    maxHeight:
                      variant === 'cart' || variant === 'fullCart'
                        ? '100%'
                        : variant === 'rune'
                          ? '185px'
                          : '160px',
                  }}
                />
              </button>
            </Tooltip>
          </Box>

          {/* Stats Description */}
          <Box sx={getStatsContainerStyles()}>
            <Typography
              variant="body2"
              component="pre"
              sx={{
                margin: 0,
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: '#000',
                textAlign:
                  variant === 'cart' ? 'left' : variant === 'fullCart' ? 'center' : 'center',
                width: '100%',
                fontSize:
                  variant === 'cart' ? '0.75rem' : variant === 'fullCart' ? '0.875rem' : 'inherit',
              }}
            >
              {/* Item Name with Rarity Indicator */}
              {showRarityIndicator && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent:
                      variant === 'cart'
                        ? 'flex-start'
                        : variant === 'fullCart'
                          ? 'center'
                          : 'center',
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Tooltip title={item.original_rarity || 'Normal'} placement="top">
                    <Box
                      sx={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: getColorFromRarity(
                          item.original_rarity || item.rarity_id || ''
                        ),
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      }}
                    />
                  </Tooltip>
                  {item.name}
                </Box>
              )}

              {/* Stats with Copy Button */}
              <Box sx={{ position: 'relative' }}>
                {item.stats_description}
                {shouldShowCopyButton() && (
                  <Tooltip title={t('common.copyToClipboard')}>
                    <IconButton
                      onClick={handleCopy}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -24,
                        right: -8,
                        color: (theme) => theme.palette.text.secondary,
                        '&:hover': {
                          color: (theme) => theme.palette.text.primary,
                        },
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Typography>
          </Box>
        </>
      ) : (
        /* Combined Image and Stats Container for default, rune, and bubble variants */
        <Box
          sx={{
            border: (theme) => `1px solid ${theme.palette.secondary.main}`,
            borderRadius: 1,
            overflow: 'hidden',
            width: '100%',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Image Section */}
          <Box sx={getImageContainerStyles()}>
            {/* Context Icons */}
            {showContext && item?.context && item.context !== '' && (
              <Tooltip
                title={getContextTooltipText(item.context, item.ladder, item.hardcore)}
                arrow
              >
                {renderContextIcons(item.context, item.ladder, item.hardcore)}
              </Tooltip>
            )}

            {/* Price Badge */}
            {showPrice &&
              (() => {
                const fiat = item.fiat_price;
                const fg = item.fg_price;
                if (fiat && fiat !== '' && fiat !== '0.00' && fiat !== '???') {
                  if (!isNaN(parseFloat(fiat)) && !fiat.toLowerCase().includes('fg')) {
                    return (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: (theme) => theme.palette.primary.main,
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                          zIndex: 1,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        }}
                      >
                        {`${parseFloat(fiat).toFixed(2).replace(/\.00$/, '')} $`}
                      </Box>
                    );
                  } else if (fiat.toLowerCase().includes('fg')) {
                    return (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: (theme) => theme.palette.primary.main,
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                          zIndex: 1,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        }}
                      >
                        {fiat}
                      </Box>
                    );
                  }
                } else if (fg && fg !== '' && fg !== '0.00') {
                  return (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: (theme) => theme.palette.primary.main,
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        fontWeight: 'bold',
                        zIndex: 1,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      }}
                    >
                      {fg.toLowerCase().includes('fg') ? fg : `${fg} fg`}
                    </Box>
                  );
                }
                return null;
              })()}

            {/* Item Image */}
            <Tooltip
              title={
                <img
                  src={'https://d2r-member.com/image/' + item.product_image_path}
                  alt={item.title || item.name}
                  style={{
                    objectFit: 'contain',
                    maxWidth: '100%',
                    maxHeight: '300px',
                  }}
                />
              }
              arrow
            >
              <button
                onClick={handleImageClick}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleImageClick();
                  }
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  width: '100%',
                  height: variant === 'rune' ? '205px' : '180px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={'https://d2r-member.com/skin/' + item.skin_image_path}
                  alt={item.name}
                  style={{
                    width: 'auto',
                    height: 'auto',
                    maxWidth: '100%',
                    objectFit: 'scale-down',
                    maxHeight: variant === 'rune' ? '185px' : '160px',
                  }}
                />
              </button>
            </Tooltip>
          </Box>

          {/* Stats Description */}
          <Box sx={getStatsContainerStyles()}>
            <Typography
              variant="body2"
              component="pre"
              sx={{
                margin: 0,
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: '#000',
                textAlign: 'center',
                width: '100%',
                fontSize: 'inherit',
              }}
            >
              {/* Item Name with Rarity Indicator */}
              {showRarityIndicator && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Tooltip title={item.original_rarity || 'Normal'} placement="top">
                    <Box
                      sx={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: getColorFromRarity(
                          item.original_rarity || item.rarity_id || ''
                        ),
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      }}
                    />
                  </Tooltip>
                  {item.name}
                </Box>
              )}

              {/* Stats with Copy Button */}
              <Box sx={{ position: 'relative' }}>
                {item.stats_description}
                {shouldShowCopyButton() && (
                  <Tooltip title={t('common.copyToClipboard')}>
                    <IconButton
                      onClick={handleCopy}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -24,
                        right: -8,
                        color: (theme) => theme.palette.text.secondary,
                        '&:hover': {
                          color: (theme) => theme.palette.text.primary,
                        },
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Typography>
          </Box>
        </Box>
      )}
    </div>
  );
};

export default ItemCard;
